import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatCwTimestampUtc } from '@/lib/cw-timestamp';
import type { AdminCloudWatchEvent, CloudWatchEventType } from '@/types/admin-trace';

// Farbschema fuer die 11 Event-Typen (Spec 143 §5.3, Referenz: Archiv-Design)
const CW_TYPE_CONFIG: Record<CloudWatchEventType, { labelKey: string; className: string }> = {
  cw_http_request:      { labelKey: 'trace.cloudwatch.types.httpRequest',      className: 'border-blue-300 bg-blue-50 text-blue-700' },
  cw_http_response:     { labelKey: 'trace.cloudwatch.types.httpResponse',     className: 'border-blue-300 bg-blue-50 text-blue-700' },
  cw_http_error:        { labelKey: 'trace.cloudwatch.types.httpError',        className: 'border-red-300 bg-red-50 text-red-700' },
  cw_webhook_received:  { labelKey: 'trace.cloudwatch.types.webhookReceived',  className: 'border-violet-300 bg-violet-50 text-violet-700' },
  cw_webhook_persisted: { labelKey: 'trace.cloudwatch.types.webhookPersisted', className: 'border-purple-300 bg-purple-50 text-purple-700' },
  cw_sqs_enqueue:       { labelKey: 'trace.cloudwatch.types.sqsEnqueue',       className: 'border-orange-300 bg-orange-50 text-orange-700' },
  cw_sqs_process:       { labelKey: 'trace.cloudwatch.types.sqsProcess',       className: 'border-orange-300 bg-orange-50 text-orange-700' },
  cw_charge_result:     { labelKey: 'trace.cloudwatch.types.chargeResult',     className: 'border-green-300 bg-green-50 text-green-700' },
  cw_notification_sent: { labelKey: 'trace.cloudwatch.types.notificationSent', className: 'border-green-300 bg-green-50 text-green-700' },
  cw_error:             { labelKey: 'trace.cloudwatch.types.error',            className: 'border-red-300 bg-red-50 text-red-700' },
  cw_log:               { labelKey: 'trace.cloudwatch.types.log',              className: 'border-gray-300 bg-gray-50 text-gray-700' },
};

const FALLBACK_TYPE = { labelKey: 'trace.cloudwatch.types.log', className: 'border-gray-300 bg-gray-50 text-gray-700' };

const HTTP_TYPES = new Set<CloudWatchEventType>(['cw_http_request', 'cw_http_response', 'cw_http_error']);

// Dynamische Umfaerbung: Erfolg/Misserfolg sichtbar machen
function resolveTypeConfig(event: AdminCloudWatchEvent) {
  const base = CW_TYPE_CONFIG[event.type] ?? FALLBACK_TYPE;

  if (event.type === 'cw_charge_result' && event.statusCode != null && event.statusCode !== 0) {
    return { ...base, className: 'border-red-300 bg-red-50 text-red-700' };
  }
  if (
    event.type === 'cw_http_response' &&
    event.statusCode != null &&
    event.statusCode >= 200 &&
    event.statusCode < 300
  ) {
    return { ...base, className: 'border-green-300 bg-green-50 text-green-700' };
  }
  // Forensik-Eskalation: ERROR-Level muss unabhaengig vom Event-Typ
  // herausstechen (z.B. cw_log mit level ERROR bliebe sonst grau)
  if (event.level === 'ERROR') {
    return { ...base, className: 'border-red-300 bg-red-50 text-red-700' };
  }
  return base;
}

const LEVEL_CLASS: Record<string, string> = {
  ERROR: 'text-red-700',
  WARN: 'text-amber-700',
  DEBUG: 'text-muted-foreground',
  INFO: 'text-muted-foreground',
};

/**
 * Einklappbarer Roh-Log (Spec 143 Akzeptanz-Anker + Trap §8.5):
 * Rohtext, NIE blind JSON.parse — Pretty-Print nur per try/catch,
 * bei Truncation gar nicht erst versuchen (abgeschnittenes JSON).
 */
function RawLogCollapsible({ rawLog, truncated }: { rawLog: string; truncated?: boolean }) {
  const { t } = useTranslation('transactions');
  const [open, setOpen] = useState(false);

  let display = rawLog;
  if (!truncated) {
    try {
      display = JSON.stringify(JSON.parse(rawLog), null, 2);
    } catch {
      // Kein valides JSON — Rohtext anzeigen
    }
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs text-muted-foreground underline hover:no-underline"
      >
        {open ? t('trace.cloudwatch.hideRaw') : t('trace.cloudwatch.showRaw')}
      </button>
      {open && (
        // Kurzes Einblenden macht den Zustandswechsel nachvollziehbar (/animate);
        // motion-reduce respektiert die OS-Einstellung
        <div className="motion-safe:animate-fade-in-down">
          {truncated && (
            <p className="mt-1 text-xs text-amber-700" data-testid="rawlog-truncated-hint">
              {t('trace.cloudwatch.truncatedHint')}
            </p>
          )}
          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 text-xs">
            {display}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Einzelnes CloudWatch-Event (Spec 143 §5.3).
 * statusCode ist kontextabhaengig (Trap §8.13): HTTP-Status nur bei
 * cw_http_*-Typen, sonst InternetQ-StatusCode (102, 155, 0=Erfolg).
 * Timestamps in UTC mit Label (Trap §8.2).
 */
export function CloudWatchEventRow({ event }: { event: AdminCloudWatchEvent }) {
  const { t } = useTranslation('transactions');
  const config = resolveTypeConfig(event);
  const isHttp = HTTP_TYPES.has(event.type);

  return (
    <div className="border-l-2 border-border pl-3" data-testid="cw-event-row">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={config.className}>
          {t(config.labelKey, event.type)}
        </Badge>
        {event.level && (
          <span className={cn('text-xs font-medium', LEVEL_CLASS[event.level] ?? 'text-muted-foreground')}>
            {event.level}
          </span>
        )}
        {event.phase && <span className="text-xs text-muted-foreground">{event.phase}</span>}
        {event.timestamp && (
          <span className="font-mono text-xs text-muted-foreground">
            {formatCwTimestampUtc(event.timestamp)}
          </span>
        )}
      </div>
      {event.message && <p className="mt-0.5 break-words text-sm">{event.message}</p>}
      <p className="mt-0.5 text-xs text-muted-foreground">
        {event.statusCode != null && (
          <span data-testid="cw-status-code">
            {isHttp
              ? `HTTP ${event.statusCode}`
              : `${t('trace.cloudwatch.statusCode')}: ${event.statusCode}`}
          </span>
        )}
        {event.statusCode != null && event.durationMs != null && ' · '}
        {event.durationMs != null && <>{event.durationMs} ms</>}
        {(event.statusCode != null || event.durationMs != null) && event.logGroup && ' · '}
        {event.logGroup && <span className="font-mono">{event.logGroup}</span>}
      </p>
      {event.rawLog && (
        <RawLogCollapsible
          rawLog={event.rawLog}
          {...(event.rawLogTruncated != null ? { truncated: event.rawLogTruncated } : {})}
        />
      )}
    </div>
  );
}
